
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../../integrations/supabase/client';
import { Calendar, MapPin, Users, Briefcase, AlertCircle, Loader2, Car, DollarSign, ChevronDown, ChevronUp } from 'lucide-react';
import type { Database } from '../../../integrations/supabase/types';
import { useState } from 'react';

type Booking = Database['public']['Tables']['bookings']['Row'];

const Bookings = () => {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');
  const [expandedBooking, setExpandedBooking] = useState<string | null>(null);

  const { data: bookings, isLoading, error } = useQuery({
    queryKey: ['bookings', statusFilter],
    queryFn: async () => {
      console.log('Fetching bookings data...');
      let query = supabase
        .from('bookings')
        .select('*, vehicles(name)');
      
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      const { data, error } = await query.order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching ride bookings:', error);
        throw error;
      }
      
      console.log('Bookings fetched:', data?.length || 0);
      return data as (Booking & { vehicles: { name: string } | null })[];
    },
    refetchOnWindowFocus: false
  });

  const updateBookingStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('bookings')
        .update({ status })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    }
  });

  const deleteBooking = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bookings')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    }
  });

  const toggleExpand = (id: string) => {
    setExpandedBooking(expandedBooking === id ? null : id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
        <span className="ml-2">Loading bookings...</span>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          <h3 className="text-lg font-medium">Error Loading Bookings</h3>
        </div>
        <p className="mt-1">There was a problem loading the bookings data.</p>
        <p className="mt-2 text-sm font-mono bg-red-100 p-2 rounded">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Manage Bookings</h2>
        <div className="flex gap-2">
          <select
            className="border rounded-md px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="grid gap-6">
        {bookings && bookings.length > 0 ? (
          bookings.map((booking) => (
            <div key={booking.id} className="bg-white rounded-lg shadow p-6 space-y-4">
              <div className="flex justify-between items-start">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <span className="font-medium">
                      {booking.pickup_location ? 
                        (typeof booking.pickup_location === 'string' ? 
                          booking.pickup_location : 
                          (booking.pickup_location as any).address || 'Unknown location') : 
                        'Unknown location'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-gray-500 rotate-180" />
                    <span>
                      {booking.dropoff_location ? 
                        (typeof booking.dropoff_location === 'string' ? 
                          booking.dropoff_location : 
                          (booking.dropoff_location as any).address || 'Unknown location') : 
                        'Unknown location'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Car className="w-5 h-5 text-gray-500" />
                    <span>{booking.vehicles?.name || 'No vehicle selected'}</span>
                  </div>
                </div>
                <div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800' : 
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                    booking.status === 'completed' ? 'bg-purple-100 text-purple-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {booking.status}
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4 pt-4 border-t">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <span>{new Date(booking.pickup_date).toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-500" />
                    <span>{booking.passenger_count} passengers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-gray-500" />
                    <span>€{booking.total_price}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-gray-500" />
                    <span className="text-sm">{booking.passenger_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-gray-500" />
                    <span className="text-sm">{booking.passenger_email}</span>
                  </div>
                  {booking.special_requests && (
                    <div className="flex items-start gap-2">
                      <Briefcase className="w-5 h-5 text-gray-500 mt-1" />
                      <p className="text-sm text-gray-600">{booking.special_requests}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between items-center pt-4 border-t">
                <button 
                  onClick={() => toggleExpand(booking.id)}
                  className="text-sm flex items-center gap-1 text-gray-600 hover:text-gray-900"
                >
                  {expandedBooking === booking.id ? (
                    <>Less details <ChevronUp className="w-4 h-4" /></>
                  ) : (
                    <>More details <ChevronDown className="w-4 h-4" /></>
                  )}
                </button>
                <div className="flex gap-3">
                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => updateBookingStatus.mutate({ id: booking.id, status: 'confirmed' })}
                        className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => updateBookingStatus.mutate({ id: booking.id, status: 'cancelled' })}
                        className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <button
                      onClick={() => updateBookingStatus.mutate({ id: booking.id, status: 'completed' })}
                      className="px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors"
                    >
                      Complete
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this booking?')) {
                        deleteBooking.mutate(booking.id);
                      }
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {expandedBooking === booking.id && (
                <div className="pt-4 mt-4 border-t">
                  <h4 className="font-medium mb-3">Detailed Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm"><span className="font-medium">Booking ID:</span> {booking.id}</p>
                      <p className="text-sm"><span className="font-medium">Created:</span> {new Date(booking.created_at).toLocaleString()}</p>
                      <p className="text-sm"><span className="font-medium">Last Updated:</span> {new Date(booking.updated_at).toLocaleString()}</p>
                      {booking.distance_km && (
                        <p className="text-sm"><span className="font-medium">Distance:</span> {booking.distance_km} km</p>
                      )}
                      <p className="text-sm"><span className="font-medium">Phone:</span> {booking.passenger_phone}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium">Total: €{booking.total_price}</p>
                      {booking.special_requests && (
                        <p className="text-sm mt-2"><span className="font-medium">Special Requests:</span> {booking.special_requests}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-12 bg-white rounded-lg">
            <p className="text-gray-500">No bookings found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Bookings;
